import { isClassicEditor, panelDefs } from '@wix/wix-data-client-common'
import type { I18nextProviderProps } from 'react-i18next'
import type {
  EDITOR_TYPE,
  DatasetType,
  Sort,
} from '@wix/wix-data-client-common'
import type { DS } from '@wix/editorx-core-api'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import {
  DESIGN_SYSTEMS,
  type DigestedSchemaField,
  type EditorSDK,
} from '@wix/wix-data-client-common-standalone'

const getPanelHeight = ({
  isMirroringTipVisible,
  editorType,
}: {
  isMirroringTipVisible: boolean
  editorType: EDITOR_TYPE
}) => {
  if (isClassicEditor(editorType)) {
    return isMirroringTipVisible ? 367 : 254
  }

  return isMirroringTipVisible ? 317 : 215
}

interface GetDefaultPanelSettingsParams {
  hostname: string
  editorSdkProxy: EditorSDK
  isMirroringTipVisible: boolean
  editorType: EDITOR_TYPE
}

const getDefaultPanelSettings = async ({
  hostname,
  editorSdkProxy,
  isMirroringTipVisible,
  editorType,
}: GetDefaultPanelSettingsParams) => {
  return {
    width: 288,
    height: getPanelHeight({
      isMirroringTipVisible,
      editorType,
    }),
    noPanelPadding: true,
    url: await getPanelUrl({
      hostname,
      relativeUrl: panelDefs.sortPanel.url,
      editorSdkProxy,
      params: {
        designSystemsToLoad: [DESIGN_SYSTEMS.WDS],
      },
    }),
  }
}

interface PanelServices {
  hostname: string
  editorSdkProxy: EditorSDK
  i18n: I18nextProviderProps['i18n']
  editorType: EDITOR_TYPE
}

export const openAddSortPanel = async (
  { hostname, editorSdkProxy, i18n, editorType }: PanelServices,
  {
    fields,
    controllerRef,
    controllerType,
    collectionId,
    sortIsMirrored,
    hasSorts,
  }: {
    fields: DigestedSchemaField[]
    controllerRef: DS.CompRef
    controllerType: DatasetType
    collectionId: string
    sortIsMirrored: boolean
    hasSorts: boolean
  },
): Promise<Sort | undefined> => {
  const isMirroringTipVisible = !hasSorts && !sortIsMirrored

  const defaultPanelSettings = await getDefaultPanelSettings({
    hostname,
    editorSdkProxy,
    isMirroringTipVisible,
    editorType,
  })

  return editorSdkProxy.editor.openToolPanel({
    ...defaultPanelSettings,

    title: i18n.t('DATASET_SETTINGS_ADD_SORT_PANEL_TITLE'),
    initialData: {
      fields,
      controllerRef,
      controllerType,
      collectionId,
      sortIsMirrored,
      isMirroringTipVisible,
    },
  })
}

export const openEditSortPanel = async (
  { hostname, editorSdkProxy, i18n, editorType }: PanelServices,
  {
    fields,
    controllerRef,
    controllerType,
    sortExpression,
    collectionId,
    sortIsMirrored,
  }: {
    fields: DigestedSchemaField[]
    controllerRef: DS.CompRef
    controllerType: DatasetType
    sortExpression: Sort
    collectionId: string
    sortIsMirrored: boolean
  },
): Promise<Sort | undefined> => {
  const defaultPanelSettings = await getDefaultPanelSettings({
    hostname,
    editorSdkProxy,
    isMirroringTipVisible: false,
    editorType,
  })

  return editorSdkProxy.editor.openToolPanel({
    ...defaultPanelSettings,

    title: i18n.t('DATASET_SETTINGS_EDIT_SORT_PANEL_TITLE'),
    initialData: {
      fields,
      sortExpression,
      controllerRef,
      controllerType,
      collectionId,
      sortIsMirrored,
      isMirroringTipVisible: false,
    },
  })
}
